import AuthLayout from "./AuthLayout";
import { useNavigate } from "react-router-dom";
import { Button, TextField } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useState } from "react";
import { useSendOtpMutation } from "../../store/api/authAPI";
import { showToast, showError } from "../../constants/toasts";

interface FormErrors {
  email?: string;
}

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [sendOtpMutation, { isLoading: isSendingOtp }] = useSendOtpMutation();
  const [formData, setFormData] = useState({
    email: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      showError("Please fix the errors in the form");
      return;
    }

    try {
      const response = await sendOtpMutation({
        email: formData.email.trim(),
        otpType: 3, // OTP type for forgot password
      }).unwrap();

      if (response) {
        showToast(
          response.message || "OTP has been sent to your email address"
        );
        // Navigate to OTP verification page after successful submission
        navigate("/otp", { state: { email: formData.email.trim() } });
      }
    } catch (error: any) {
      // Handle error response
      let errorMessage =
        "Failed to send OTP. Please try again later.";

      // Handle PARSING_ERROR (when server returns HTML instead of JSON)
      if (
        error?.status === "PARSING_ERROR" ||
        error?.error?.status === "PARSING_ERROR"
      ) {
        const originalStatus =
          error?.originalStatus || error?.error?.originalStatus;
        if (originalStatus === 404) {
          errorMessage = "Endpoint not found. Please check the API configuration.";
        } else {
          errorMessage =
            "Server returned an invalid response. Please check the API endpoint.";
        }
        console.error("PARSING_ERROR details:", error);
      } else {
        // Try to extract error message from various error formats
        errorMessage =
          error?.data?.message ||
          error?.data?.error ||
          error?.error?.data?.message ||
          error?.error?.data?.error ||
          error?.message ||
          errorMessage;
      }

      showError(errorMessage);
    }
  };

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Forgot Password</h2>
        <p>Please provide your email address to reset your password</p>

        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group">
            <label htmlFor="email">Email Address</label>
            <TextField
              id="email"
              name="email"
              type="email"
              placeholder="Enter Email Address"
              fullWidth
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email || " "}
              FormHelperTextProps={{
                style: { margin: 0, minHeight: errors.email ? "auto" : "20px" }
              }}
              disabled={isSendingOtp}
            />
          </div>

          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary w_100"
              variant="contained"
              disabled={isSendingOtp}
            >
              {isSendingOtp ? "Sending..." : "Submit"}
            </Button>
          </div>
        </form>

        <div className="auth_bottom">
          <p>
            <button
              type="button"
              className="link"
              onClick={() => navigate("/")}
              disabled={isSendingOtp}
            >
              <KeyboardBackspaceIcon /> Back to Login
            </button>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
